// The ONE definition of "which shell does this viewport get" (#432).
//
// The app has two shells: the DESKTOP shell (the 250px Features Sidebar, the
// conversation column, two side-by-side view slots) and the MOBILE shell
// (Mobile Visual #171: one full-screen column driven by the overlay menu).
// Both the JS (`useIsMobile`) and the CSS (Tailwind's `shell:` variant,
// tailwind.config.js) read the queries below, so the two can never disagree
// about which shell is on screen — the bug a raw `md:` + a separate JS number
// would invite.
//
// The rule, and why:
//   • ≥ 1280px wide → DESKTOP, at ANY height. That is the design system's own
//     shell and the viewport the rest of the E2E suite runs at; nothing there
//     may change (some specs deliberately squeeze the HEIGHT to 320px).
//   • 1024–1279px wide → DESKTOP, unless the viewport is 500px tall or less
//     (a phone held sideways), which gets the mobile shell.
//   • < 1024px wide → MOBILE. Tablets in portrait (768/820/834) cannot hold the
//     desktop shell's floors: 250px sidebar + 300px conversation column + 28px
//     frame + gutters leave an 820px iPad 214px of chat, under the 240px
//     CHAT_MIN_WIDTH every #243 containment contract is written against.
//
// It used to be width-only at 768px (Tailwind `md`), with the desktop shell
// pinned to a 1280px floor that scrolled sideways — which is what put every
// tablet's right-hand controls outside the viewport.

export const SHELL_FULL_DESKTOP_MIN_WIDTH = 1280;
export const SHELL_DESKTOP_MIN_WIDTH = 1024;
export const SHELL_SHORT_MAX_HEIGHT = 500;

// Desktop shell: either wide enough outright, or tablet-landscape wide AND tall.
export const DESKTOP_SHELL_QUERY =
    `(min-width: ${SHELL_FULL_DESKTOP_MIN_WIDTH}px), `
    + `(min-width: ${SHELL_DESKTOP_MIN_WIDTH}px) and (min-height: ${SHELL_SHORT_MAX_HEIGHT + 1}px)`;

// The exact complement of DESKTOP_SHELL_QUERY (CSS `not` does not distribute
// over a query list, so it is spelled out; the unit test pins the complement).
export const MOBILE_SHELL_QUERY =
    `(max-width: ${SHELL_DESKTOP_MIN_WIDTH - 0.02}px), `
    + `(max-width: ${SHELL_FULL_DESKTOP_MIN_WIDTH - 0.02}px) and (max-height: ${SHELL_SHORT_MAX_HEIGHT}px)`;

/** Pure form of the rule — used by the tests and by anything that has numbers, not a MediaQueryList. */
export function isDesktopShell(width, height) {
    if (width >= SHELL_FULL_DESKTOP_MIN_WIDTH) return true;
    return width >= SHELL_DESKTOP_MIN_WIDTH && height > SHELL_SHORT_MAX_HEIGHT;
}
